import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

// Types
import { ItemType } from 'Shared/constants/types';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Helpers
import { contentStateFromLink } from 'Shared/components/DraftEditor/plugins/smartLinksPlugin';
import { getSurfaceItemTrackingProperties } from 'Shared/helpers';

// Constants
import { addedSurfaceItemToAgenda } from 'Shared/constants/constants';

// Actions
import { addItem } from 'Shared/actions/todos';

// Components
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WorkspacesSelect from 'Shared/components/WorkspaceSelect/WorkspaceSelect';
import SurfaceItemLogo from 'Shared/components/SurfaceAI/SurfaceRowComponents/SurfaceItemLogo';
import SurfaceItemName from 'Shared/components/SurfaceAI/SurfaceRowComponents/SurfaceItemName';
import SurfaceItemStatus from 'Shared/components/SurfaceAI/SurfaceRowComponents/SurfaceItemStatus';

// Styles
import './AddSurfaceItemToWorkspaceModal.scss';

type Props = {
  isOpen: boolean;
  toggleModal: () => void;
  surfaceItem;
};

const AddSurfaceItemToWorkspaceModal = ({ isOpen, surfaceItem, toggleModal }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [isSaving, toggleIsSaving] = useToggle(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);

  const { workspaceId } = useParams();
  const workspaces = useSelector(state => state.workspaces);

  const handleViewBtnClick = (workspaceId, itemUuid) => {
    history.push(PageUrlsUtil.makeWorkspaceMeetingItemCommentsUrl(workspaceId, itemUuid));
  };

  const handleAddToWorkspace = () => {
    toggleIsSaving();
    const body = JSON.stringify(
      contentStateFromLink({ linkText: surfaceItem.name, linkHref: surfaceItem.external_link })
    );

    const itemUuid = uuidv4();
    return dispatch(
      addItem({
        itemUuid,
        body,
        workspaceId: selectedWorkspace.id,
        position: 1,
        itemType: selectedItemType,
      })
    ).then(() => {
      toggleIsSaving();
      toggleModal();
      const trackingProps = getSurfaceItemTrackingProperties({
        surfaceItem,
        type: selectedItemType,
        workspace: selectedWorkspace,
      });
      (window as any).analytics.track(addedSurfaceItemToAgenda, trackingProps);
      const toastTitle = selectedItemType === ItemType.actionItem ? 'Action item created' : 'Topic created';
      WpToastManager.success(toastTitle, {
        actionBtnLabel: 'view',
        actionBtnFnc: () => handleViewBtnClick(selectedWorkspace.id, itemUuid),
      });
    });
  };

  useEffect(() => {
    if (workspaceId && workspaces) {
      setSelectedWorkspace(workspaces.find(ws => ws.id === workspaceId));
    }
  }, [workspaceId, workspaces, setSelectedWorkspace]);

  const baseClassName = 'AddSurfaceItemToWorkspaceModal';
  return (
    <WpModal isOpen={isOpen} size="sm" onToggle={toggleModal} dismissOnBackdrop={false}>
      <WpModalHeader>Add to workspace</WpModalHeader>
      <WpModalBody>
        <div className={baseClassName}>
          <p>Moving an item to a workspace will add it to your collaborative meeting agenda with them.</p>
          <div className={`${baseClassName}__item`}>
            <div className={`${baseClassName}__item__logo`}>
              <SurfaceItemLogo surfaceItem={surfaceItem} />
            </div>
            <div className={`${baseClassName}__item__name`}>
              <SurfaceItemName surfaceItem={surfaceItem} />
            </div>
            <div className={`${baseClassName}__item__status`}>
              <SurfaceItemStatus surfaceItem={surfaceItem} />
            </div>
          </div>
          <div className={`${baseClassName}__select`}>
            <WorkspacesSelect
              id="workspaces-select"
              placeholder="select workspace"
              workspaces={workspaces}
              value={workspaceId}
              onChange={setSelectedWorkspace}
            />
          </div>
          <div className={`${baseClassName}__select`}>
            <WpRadioGroup
              id="role_input"
              label="Create Item as"
              defaultValue={ItemType.discussionTopic}
              labelHidden
              onChange={setSelectedItemType}
            >
              <WpRadioInput id={ItemType.discussionTopic} label="Discussion topic" value={ItemType.discussionTopic} />
              <WpRadioInput id={ItemType.actionItem} label="Action item" value={ItemType.actionItem} />
            </WpRadioGroup>
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={toggleModal}>
            Cancel
          </WpButton>
          <WpButton loading={isSaving} onClick={handleAddToWorkspace} disabled={!selectedWorkspace}>
            Add to workspace
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default AddSurfaceItemToWorkspaceModal;
